import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "./LoadingIndicator";
import { CloseIcon } from "./Icon";
import { sidebarStateManager } from "../utils/SidebarStateManager";
import { useSidebarState } from "../hooks/useSidebarState";
import { useSidebarOpenEffect } from "../hooks/useSidebarOpenEffect";
import { SidebarType } from "../utils/SidebarStateManager";
import AnalyticsManager from "../utils/AnalyticsManager";
import RestoApiService, { ChainListResponse, ChainSummary } from "../utils/RestoApiService";
import { getChainPath } from "../utils/RestoSlugUtils";
import styles from "./RestaurantDetailsSidebar.module.css";

interface ChainRankingsContentProps {
  initialData?: ChainListResponse;
}

type SortMode = "size" | "scoreDesc" | "scoreAsc";

const getScoreEmoji = (score: number) => {
  if (score === 5) return "😍";
  if (score === 4) return "😃";
  if (score === 3) return "🙂";
  if (score === 2) return "🤮";
  if (score === 1) return "💀";
  return "❓";
};

const restoApiService = new RestoApiService();

const ChainRankingsContent: React.FC<ChainRankingsContentProps> = ({ initialData }) => {
  const { t, i18n } = useTranslation();
  const { closeSidebar } = useSidebarState(sidebarStateManager);
  const [data, setData] = useState<ChainListResponse | null>(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [sortMode, setSortMode] = useState<SortMode>("size");
  const [minSize, setMinSize] = useState(5);
  const lang = i18n.language || 'en';

  const fetchRankings = async () => {
    setLoading(true);
    // One fetch at the lowest threshold; sorting and minSize filter happen client-side
    const result = await restoApiService.fetchChains({ sort: 'size', order: 'desc', minSize: 3, limit: 500 });
    if (result.success && result.data) setData(result.data);
    setLoading(false);
  };

  useEffect(() => {
    if (!initialData) fetchRankings();
    AnalyticsManager.getInstance().trackEvent('chain_ranking_view', {
      min_size: minSize,
      event_category: 'content',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch the full list when the sidebar opens and we only have the SSR subset
  useSidebarOpenEffect(SidebarType.CHAIN_RANKINGS, () => {
    if (data && data.chains.length < 500 && data.minSize > 3) fetchRankings();
  });

  const visibleChains = useMemo(() => {
    if (!data) return [];
    const filtered = data.chains.filter((c) => c.locationCount >= minSize);
    const list = [...filtered];
    if (sortMode === 'scoreDesc') {
      list.sort((a, b) => (b.avgScore ?? -1) - (a.avgScore ?? -1) || b.locationCount - a.locationCount);
    } else if (sortMode === 'scoreAsc') {
      list.sort((a, b) => (a.avgScore ?? 6) - (b.avgScore ?? 6) || b.locationCount - a.locationCount);
    } else {
      list.sort((a, b) => b.locationCount - a.locationCount);
    }
    return list;
  }, [data, sortMode, minSize]);

  const handleChainClick = (e: React.MouseEvent, chain: ChainSummary, rank: number) => {
    e.preventDefault();
    AnalyticsManager.getInstance().trackEvent('chain_ranking_chain_click', {
      chain_slug: chain.slug,
      rank,
      event_category: 'navigation',
    });
    // Close (and unregister) the sidebar BEFORE dispatching popstate —
    // NavigationManager intercepts popstate in capture phase while an
    // overlay is registered and would swallow the navigation as a "back"
    closeSidebar();
    window.history.pushState({}, '', `/${lang}/${getChainPath(lang)}/${chain.slug}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const setSort = (mode: SortMode) => {
    setSortMode(mode);
    AnalyticsManager.getInstance().trackEvent('chain_ranking_sort', {
      sort: mode,
      event_category: 'engagement',
    });
  };

  const sortOptions: { mode: SortMode; label: string }[] = [
    { mode: 'size', label: t('chain.rankings.sortBySize', 'Locations') },
    { mode: 'scoreDesc', label: t('chain.rankings.sortByScoreDesc', 'Score: high → low') },
    { mode: 'scoreAsc', label: t('chain.rankings.sortByScoreAsc', 'Score: low → high') },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className={`flex-grow overflow-y-auto overflow-x-hidden ${styles.customScrollbar}`}>
        <div className="pl-4 pr-2 pt-4 pb-16">
          {/* Header */}
          <div className="flex justify-between items-center mb-1">
            <div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                {t('chain.rankings.title', 'Chains compared')}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('chain.rankings.subtitle', 'Average FAVV inspection scores per chain')}
              </p>
            </div>
            <button
              onClick={closeSidebar}
              className="p-2 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 mt-2 leading-relaxed">
            {t('chain.rankings.intro', 'Compare supermarket, fast food and retail chains by the average official FAVV inspection score of their locations.')}
          </p>

          {/* Controls */}
          <div className="flex gap-1.5 mb-2 flex-wrap">
            {sortOptions.map(({ mode, label }) => (
              <button
                key={mode}
                onClick={() => setSort(mode)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  sortMode === mode
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-gray-500 dark:text-gray-400">{t('chain.rankings.minSize', 'Min. locations')}:</span>
            {[3, 5, 10].map((n) => (
              <button
                key={n}
                onClick={() => setMinSize(n)}
                className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                  minSize === n
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {n}+
              </button>
            ))}
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <LoadingIndicator isLoading={true} />
            </div>
          )}

          {!loading && visibleChains.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                {t('chain.rankings.noData', 'No chain data available yet')}
              </p>
            </div>
          )}

          {!loading && visibleChains.length > 0 && (
            <div className="space-y-1.5" data-testid="chain-rankings-list">
              {visibleChains.map((chain, i) => (
                <a
                  key={chain.slug}
                  href={`/${lang}/${getChainPath(lang)}/${chain.slug}`}
                  onClick={(e) => handleChainClick(e, chain, i + 1)}
                  className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors flex items-center gap-3 no-underline"
                >
                  <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 w-7 flex-shrink-0 text-right">
                    {i + 1}.
                  </span>
                  <div className="min-w-0 flex-1">
                    <h5 className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{chain.name}</h5>
                    <p className="text-xs text-gray-400">
                      {t('chain.rankings.locationCount', '{{count}} locations', { count: chain.locationCount })}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                      {chain.avgScore != null ? chain.avgScore.toFixed(1) : '—'}
                      <span className="text-xs text-gray-400 font-normal">/5</span>
                    </span>
                    <span className="text-lg">{getScoreEmoji(Math.round(chain.avgScore ?? 0))}</span>
                  </div>
                </a>
              ))}
            </div>
          )}

          {/* Methodology / legal framing */}
          <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed mt-4">
            {t('chain.methodology', 'Averages are based on official FAVV inspection results per location. Locations are grouped by name and may belong to different operators.')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChainRankingsContent;
